import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { convertMediaUrl, environmentConfig } from '../../config/environment';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Card,
  CardMedia,
  useTheme,
  useMediaQuery,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
} from '@mui/material';
import {
  Close,
  Download,
  Edit,
  Delete,
  Fullscreen,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Transform,
  CheckCircle,
  HourglassEmpty,
  Schedule,
  Error,
  Help,
} from '@mui/icons-material';

import { MediaType, MediaStatus } from '../../types';
import type { Media } from '../../types';
import apiClient from '../../services/api';
import { ConversionDialog } from './index';

interface MediaViewerProps {
  media: Media | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (media: Media) => void;
  onDelete?: (media: Media) => void;
}

const MediaViewer: React.FC<MediaViewerProps> = ({
  media,
  open,
  onClose,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isConversionOpen, setIsConversionOpen] = useState(false);



  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [mediaObjectUrl, setMediaObjectUrl] = useState<string | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaLoadError, setMediaLoadError] = useState<string | null>(null);

  // Добавляем состояние для конвертаций
  const [conversionJobs, setConversionJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Загружаем историю конвертаций
  useEffect(() => {
    if (media?.id) {
      loadConversionHistory();
    }
  }, [media?.id]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!open || !media?.id) {
      setMediaObjectUrl(null);
      setMediaLoading(false);
      setMediaLoadError(null);
      return undefined;
    }

    setMediaLoading(true);
    setMediaLoadError(null);
    setImageError(false);
    apiClient.getMediaBlob(media.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaObjectUrl(objectUrl);
      })
      .catch((error) => {
        if (cancelled) return;
        setMediaLoadError(error?.detail || error?.message || 'Не удалось загрузить файл');
      })
      .finally(() => {
        if (!cancelled) setMediaLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, media?.id]);

  const loadConversionHistory = async () => {
    try {
      setLoadingJobs(true);
      const response = await apiClient.request('GET', `/media/${media.id}/conversions`);
      setConversionJobs(response);
    } catch (error) {
      console.error('Ошибка загрузки истории конвертаций:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Функция для получения иконки статуса конвертации
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'processing': return <HourglassEmpty color="warning" />;
      case 'queued': return <Schedule color="info" />;
      case 'failed': return <Error color="error" />;
      default: return <Help />;
    }
  };

  // Функция для получения цвета прогресса
  const getProgressColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'primary';
      case 'failed': return 'error';
      default: return 'inherit';
    }
  };

  if (!media) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${remainingMinutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  const getStatusColor = (status: MediaStatus) => {
    switch (status) {
      case MediaStatus.READY:
        return 'success';
      case MediaStatus.PROCESSING:
        return 'warning';
      case MediaStatus.ERROR:
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDownload = () => {
    const downloadUrl = apiClient.getMediaDownloadUrl(media.id);
    window.open(downloadUrl, '_blank');
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(media);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(media);
    }
  };

  const renderMediaPreview = () => {
    // Проверяем доступность URL'ов
    const fileUrl = mediaObjectUrl || (media.file_url ? convertMediaUrl(media.file_url) : '');
    const thumbnailUrl = media.thumbnail_url ? convertMediaUrl(media.thumbnail_url) : null;

    if (mediaLoading) {
      return (
        <Card sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Card>
      );
    }

    if (mediaLoadError) {
      return (
        <Card sx={{ p: 3 }}>
          <Typography variant="body2" color="error">
            {mediaLoadError}
          </Typography>
        </Card>
      );
    }

    switch (media.type) {
      case MediaType.IMAGE:
        return (
          <Card sx={{ maxWidth: '100%', maxHeight: 500 }}>
            {!imageError && fileUrl ? (
              <CardMedia
                component="img"
                image={fileUrl}
                alt={media.title}
                onError={() => {
                  console.log(`Ошибка загрузки изображения: ${fileUrl}`);
                  setImageError(true);
                }}
                onLoad={() => setImageError(false)}
                sx={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  maxHeight: 500,
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                  p: 3
                }}
              >
                <Typography variant="h6" gutterBottom>
                  🖼️ {media.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {imageError ? 'Ошибка загрузки изображения' : 'Изображение недоступно'}
                </Typography>
              </Box>
            )}
          </Card>
        );

      case MediaType.VIDEO:
        return (
          <Card sx={{ maxWidth: '100%' }}>
            {fileUrl ? (
              <video
                controls
                style={{
                  width: '100%',
                  maxHeight: 500,
                  objectFit: 'contain',
                }}
                poster={thumbnailUrl || undefined}
                onError={(e) => {
                  console.log(`Ошибка загрузки видео: ${fileUrl}`, e);
                }}
              >
                <source src={fileUrl} type={media.mime_type || 'video/mp4'} />
                {t('media.videoNotSupported')}
              </video>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                  p: 3
                }}
              >
                <Typography variant="h6" gutterBottom>
                  🎬 {media.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Видео недоступно
                </Typography>
              </Box>
            )}
          </Card>
        );

      case MediaType.AUDIO:
        return (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              🎵 {media.title}
            </Typography>
            {fileUrl ? (
              <audio
                controls
                style={{ width: '100%', marginTop: 16 }}
                onError={(e) => {
                  console.log(`Ошибка загрузки аудио: ${fileUrl}`, e);
                }}
              >
                <source src={fileUrl} type={media.mime_type || 'audio/mpeg'} />
                {t('media.audioNotSupported')}
              </audio>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Аудиофайл недоступен
              </Typography>
            )}
          </Card>
        );

      case MediaType.PDF:
        return (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              📄 {media.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('media.pdfPreviewNotAvailable')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<Download />}
            >
              {t('media.downloadToView')}
            </Button>
          </Card>
        );

      case MediaType.HTML:
        return (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              🌐 {media.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('media.htmlPreviewNotAvailable')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<Download />}
            >
              {t('media.downloadToView')}
            </Button>
          </Card>
        );

      default:
        return (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              📁 {media.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('media.previewNotAvailable')}
            </Typography>
          </Card>
        );
    }
  };

  const metadataRows = [
    { label: t('media.filename'), value: media.filename },
    { label: t('media.fileSize'), value: formatFileSize(media.file_size) },
    { label: t('media.type'), value: media.type?.toUpperCase() || 'UNKNOWN' },
    { label: t('media.status'), value: (
      <Chip
                        label={t(`media.statuses.${media.status.toLowerCase()}`)}
        color={getStatusColor(media.status)}
        size="small"
      />
    )},

    // Базовые параметры
    ...(media.width && media.height ? [{ label: t('media.resolution'), value: `${media.width}x${media.height}` }] : []),
    ...(media.duration ? [{ label: t('media.duration'), value: formatDuration(media.duration) }] : []),

    // Расширенные видео метаданные
    ...(media.type === 'video' && media.media_metadata ? [
      { label: t('media.codec'), value: media.media_metadata.codec || 'N/A' },
      { label: t('media.profile'), value: media.media_metadata.profile || 'N/A' },
      { label: t('media.framerate'), value: media.media_metadata.framerate ? `${media.media_metadata.framerate} fps` : 'N/A' },
      { label: t('media.aspectRatio'), value: media.media_metadata.aspect_ratio || 'N/A' },
      { label: t('media.bitrate'), value: media.media_metadata.bit_rate ? `${Math.round(media.media_metadata.bit_rate / 1000)} kbps` : 'N/A' },
      { label: t('media.pixelFormat'), value: media.media_metadata.pixel_format || 'N/A' },
      { label: t('media.containerFormat'), value: media.media_metadata.container_format || 'N/A' },

      // Аудио информация для видео
      ...(media.media_metadata.audio_codec ? [
        { label: t('media.audioCodec'), value: media.media_metadata.audio_codec },
        { label: t('media.audioChannels'), value: media.media_metadata.channels ? `${media.media_metadata.channels} ch` : 'N/A' },
        { label: t('media.sampleRate'), value: media.media_metadata.sample_rate ? `${media.media_metadata.sample_rate} Hz` : 'N/A' },
        { label: t('media.audioBitrate'), value: media.media_metadata.audio_bit_rate ? `${Math.round(media.media_metadata.audio_bit_rate / 1000)} kbps` : 'N/A' }
      ] : [])
    ] : []),

    // Аудио метаданные для аудиофайлов
    ...(media.type === 'audio' && media.media_metadata ? [
      { label: t('media.audioCodec'), value: media.media_metadata.codec || 'N/A' },
      { label: t('media.audioChannels'), value: media.media_metadata.channels ? `${media.media_metadata.channels} ch` : 'N/A' },
      { label: t('media.sampleRate'), value: media.media_metadata.sample_rate ? `${media.media_metadata.sample_rate} Hz` : 'N/A' },
      { label: t('media.audioBitrate'), value: media.media_metadata.bit_rate ? `${Math.round(media.media_metadata.bit_rate / 1000)} kbps` : 'N/A' }
    ] : []),

    { label: t('media.uploadedAt'), value: new Date(media.created_at).toLocaleString() },
    ...(media.updated_at ? [{ label: t('media.updatedAt'), value: new Date(media.updated_at).toLocaleString() }] : []),
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: isMobile ? '100vh' : '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" noWrap>
          {media.title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container spacing={0} sx={{ height: '100%' }}>
          {/* Превью медиафайла */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {renderMediaPreview()}
            </Box>
          </Grid>

          {/* Информация о файле */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ p: 2, height: '100%', backgroundColor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                {t('media.fileInfo')}
              </Typography>

              {/* Описание */}
              {media.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('media.description')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {media.description}
                  </Typography>
                </Box>
              )}

              {/* Теги */}
              {media.tags && media.tags.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('media.tags')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {media.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Метаданные */}
              <Typography variant="subtitle2" gutterBottom>
                {t('media.metadata')}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {metadataRows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', width: '40%' }}>
                          {row.label}
                        </TableCell>
                        <TableCell>
                          {typeof row.value === 'string' ? (
                            <Typography variant="body2">
                              {row.value}
                            </Typography>
                          ) : (
                            row.value
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Технические метаданные */}
              {media.metadata && Object.keys(media.metadata).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('media.technicalInfo')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {Object.entries(media.metadata).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                              {key}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Добавляем раздел конвертаций в render */}
      {/* В секции с детальной информацией добавляем: */}

      {/* Секция конвертаций */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('media.conversions')}
        </Typography>

        {loadingJobs ? (
          <CircularProgress size={24} />
        ) : conversionJobs.length > 0 ? (
          <List>
            {conversionJobs.map((job) => (
              <ListItem key={job.job_id} divider>
                <ListItemIcon>
                  {getStatusIcon(job.status)}
                </ListItemIcon>
                <ListItemText
                  slotProps={{
                    primary: { component: 'div' },
                    secondary: { component: 'div' }
                  }}
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={job.profile_name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t(`media.conversion.statuses.${job.status}`)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="div">
                      {job.status === 'processing' ? (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={job.progress || 0}
                            color={getProgressColor(job.status)}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {job.progress || 0}%
                          </Typography>
                        </Box>
                      ) : job.status === 'completed' ? (
                        <Typography variant="caption" color="success.main">
                          {t('media.conversion.completed')} {new Date(job.completed_at).toLocaleString()}
                        </Typography>
                      ) : job.error_message ? (
                        <Typography variant="caption" color="error.main">
                          {job.error_message}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(job.created_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {job.status === 'completed' && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={async () => {
                        // Функция для скачивания конвертированного файла с авторизацией
                        try {
                          const response = await fetch(`${environmentConfig.apiBaseUrl}/api/v1/media/${media.id}/conversions/${job.job_id}/download`, {
                            method: 'GET',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                            },
                          });

                          if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                          }

                          // Получаем имя файла из Content-Disposition header
                          const contentDisposition = response.headers.get('Content-Disposition');
                          let filename = `${media.original_filename?.replace(/\.[^/.]+$/, "") || "converted"}_${job.profile_name}.mp4`;

                          if (contentDisposition) {
                            const filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/);
                            if (filenameMatch) {
                              filename = decodeURIComponent(filenameMatch[1]);
                            }
                          }

                          // Получаем blob и создаем URL для скачивания
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);

                          // Создаем ссылку для скачивания
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);

                          // Освобождаем память
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Ошибка скачивания:', error);
                          alert('Ошибка скачивания файла');
                        }
                      }}
                    >
                      <Download />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('media.conversion.noConversions')}
          </Typography>
        )}
      </Box>

      <DialogActions>
        <Button
          startIcon={<Download />}
          onClick={handleDownload}
        >
          {t('common.download')}
        </Button>

        {onEdit && (
          <Button
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            {t('common.edit')}
          </Button>
        )}

        {media && (media.media_type === 'video' || media.media_type === 'audio' || media.type === 'video' || media.type === 'audio') && (
          <Button
            startIcon={<Transform />}
            onClick={() => setIsConversionOpen(true)}
            color="secondary"
          >
            {t('media.conversion.title')}
          </Button>
        )}

        {onDelete && (
          <Button
            startIcon={<Delete />}
            onClick={handleDelete}
            color="error"
          >
            {t('common.delete')}
          </Button>
        )}

        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>

    <ConversionDialog
      open={isConversionOpen}
      onClose={() => setIsConversionOpen(false)}
      media={media}
    />
  </>
  );
};

export default MediaViewer;
